import { cn } from "@/lib/utils";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/** 判断是否为 chunk 加载失败（部署后缓存失效导致的 404） */
function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  const msg = error.message || '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    (error.name === 'TypeError' && msg.includes('import'))
  );
}

class ErrorBoundary extends Component<Props, State> {
  private autoReloadTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidUpdate(_: Props, prevState: State) {
    // 仅在生产环境且刚捕获到 chunk 加载失败时自动刷新
    if (
      !prevState.hasError &&
      this.state.hasError &&
      isChunkLoadError(this.state.error) &&
      import.meta.env.PROD
    ) {
      // 延迟 1.5s 自动刷新，让用户感知到有错误发生
      this.autoReloadTimer = setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  }

  componentWillUnmount() {
    if (this.autoReloadTimer) clearTimeout(this.autoReloadTimer);
  }

  render() {
    if (this.state.hasError) {
      const isChunkErr = isChunkLoadError(this.state.error);
      const isProduction = import.meta.env.PROD;

      // 生产环境 + chunk 加载失败：显示简洁的自动刷新提示
      if (isChunkErr && isProduction) {
        return (
          <div className="flex items-center justify-center min-h-screen p-8 bg-background">
            <div className="flex flex-col items-center w-full max-w-sm p-8 text-center">
              <RotateCcw size={40} className="text-primary mb-4 animate-spin" />
              <p className="text-base text-foreground">页面正在更新，自动刷新中...</p>
              <p className="text-sm text-muted-foreground mt-1">Refreshing page...</p>
            </div>
          </div>
        );
      }

      // 开发环境或其他错误：显示完整错误信息
      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-background">
          <div className="flex flex-col items-center w-full max-w-2xl p-8">
            <AlertTriangle
              size={48}
              className="text-destructive mb-6 flex-shrink-0"
            />

            <h2 className="text-xl mb-4">An unexpected error occurred.</h2>

            <div className="p-3 w-full rounded bg-destructive/20 mb-3">
              <p className="text-sm font-bold text-destructive">
                {this.state.error?.name}: {this.state.error?.message}
              </p>
            </div>

            <div className="p-4 w-full rounded bg-muted overflow-auto mb-6">
              <pre className="text-sm text-muted-foreground whitespace-break-spaces">
                {this.state.error?.stack}
              </pre>
            </div>

            <button
              onClick={() => window.location.reload()}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg",
                "bg-primary text-primary-foreground",
                "hover:opacity-90 cursor-pointer"
              )}
            >
              <RotateCcw size={16} />
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
